document.addEventListener('DOMContentLoaded', () => {
  const quiz = document.querySelector('[data-quiz]');
  if (!quiz) return;

  const collectionHandle = quiz.dataset.collectionHandle;

  const state = {
    style: [],
    application: [],
    color: [],
    styleAny: false,
    applicationAny: false,
    colorUnsure: false,
  };

  const totalSteps = 3;
  const progressFill = quiz.querySelector('.quiz-progress__fill');
  const progressText = quiz.querySelector('[data-current-step]');
  const loader = quiz.querySelector('.quiz-loader');

  // ── Pre-fetched data cache ──────────────────────────────────────
  let cachedHtml = null;
  let cachedStyleFilters = null;
  let cachedApplicationFilters = null;
  let cachedColorFilters = null;

  // Fetch collection HTML once on page load and cache it
  async function preFetchCollectionHtml() {
    try {
      const response = await fetch(`/collections/${collectionHandle}`);
      cachedHtml = await response.text();
      // Pre-parse all filters immediately after HTML is ready
      cachedStyleFilters = parseFacetValuesByLabel(cachedHtml, 'style');
      cachedApplicationFilters = parseFacetValuesByLabel(cachedHtml, 'application');
      cachedColorFilters = parseColorFilters(cachedHtml);
    } catch (err) {
      console.error('Failed to pre-fetch collection HTML:', err);
    }
  }

  // Synchronous parsers (work on already-fetched HTML string)
  function parseFacetValuesByLabel(html, targetLabel) {
    if (!html) return [];
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const form = doc.querySelector('#FacetFiltersForm');
    if (!form) return [];

    const detailsList = form.querySelectorAll('details.js-filter');
    const values = [];

    detailsList.forEach((details) => {
      const summary = details.querySelector('.facets__summary');
      const ariaLabel = summary?.getAttribute('aria-label') || '';
      if (ariaLabel.toLowerCase().includes(targetLabel.toLowerCase())) {
        const items = details.querySelectorAll('ul.facets-layout li input[type="checkbox"]');
        items.forEach((item) => {
          const labelEl = item.closest('li')?.querySelector('span.facet-checkbox__text-label');
          const label = labelEl?.textContent.trim();
          if (label) values.push(label);
        });
      }
    });

    return values;
  }

  function parseColorFilters(html) {
    if (!html) return {};
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const form = doc.querySelector('#FacetFiltersForm');
    if (!form) return {};

    const detailsList = form.querySelectorAll('details.js-filter');
    const colorMap = {};

    detailsList.forEach((details) => {
      const summary = details.querySelector('.facets__summary');
      const ariaLabel = summary?.getAttribute('aria-label') || '';
      if (ariaLabel.toLowerCase().includes('color')) {
        const items = details.querySelectorAll('ul.facets-layout li input[type="checkbox"]');
        items.forEach((item) => {
          const labelEl = item.closest('li')?.querySelector('span.facet-checkbox__text-label');
          const label = labelEl?.textContent.trim();
          const gid = item.value;
          if (label && gid) colorMap[label.toLowerCase()] = gid;
        });
      }
    });

    return colorMap;
  }

  // Start pre-fetching immediately on page load
  preFetchCollectionHtml();
  // ───────────────────────────────────────────────────────────────

  function updateProgress(step) {
    progressText.textContent = step;
    progressFill.style.width = `${(step / totalSteps) * 100}%`;
  }

  function updateButtons(stepEl) {
    const step = stepEl.dataset.step;
    let hasSelection = false;

    if (step === '1') hasSelection = state.style.length > 0 || state.styleAny;
    if (step === '2') hasSelection = state.application.length > 0 || state.applicationAny;
    if (step === '3') hasSelection = state.color.length > 0 || state.colorUnsure;

    const nextBtn = stepEl.querySelector('.quiz-next, .quiz-submit');
    if (nextBtn) nextBtn.disabled = !hasSelection;
  }

  function goToStep(nextStep) {
    quiz.querySelectorAll('.quiz-step').forEach((step) => step.classList.remove('active'));
    const nextEl = quiz.querySelector(`.quiz-step[data-step="${nextStep}"]`);
    if (nextEl) nextEl.classList.add('active');
    updateProgress(nextStep);
    quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  quiz.querySelectorAll('.quiz-option').forEach((option) => {
    option.addEventListener('click', () => {
      const stepEl = option.closest('.quiz-step');
      const step = stepEl.dataset.step;
      const limit = Number(stepEl.dataset.limit);

      // STEP 1 - STYLE (max 1) — now synchronous, no await needed
      if (step === '1') {
        const selectedLabel = (option.dataset.value || '').trim();
        const normalizedLabel = selectedLabel.toLowerCase();
        const isAnyStyle = normalizedLabel === 'any style';
        const stepOptions = stepEl.querySelectorAll('.quiz-option');

        if (option.classList.contains('active')) {
          option.classList.remove('active');
          state.style = [];
          state.styleAny = false;
          updateButtons(stepEl);
          return;
        }

        stepOptions.forEach((o) => o.classList.remove('active'));
        option.classList.add('active');
        state.style = [];
        state.styleAny = false;

        if (isAnyStyle) {
          state.styleAny = true;
          updateButtons(stepEl);
          return;
        }

        // Use cached data — instant, no delay
        const allStyleValues = cachedStyleFilters || [];
        const matchedValues = allStyleValues.filter((v) => {
          const normalizedValue = v.toLowerCase();
          return (
            normalizedValue === normalizedLabel ||
            normalizedValue.includes(normalizedLabel) ||
            normalizedLabel.includes(normalizedValue)
          );
        });

        state.style = matchedValues.length > 0 ? [matchedValues[0]] : [selectedLabel];
        updateButtons(stepEl);
        return;
      }

      // STEP 2 - APPLICATION (max 1) — now synchronous
      if (step === '2') {
        const selectedLabel = option.textContent.trim();
        const normalizedLabel = selectedLabel.toLowerCase();
        const isAnyRoom = normalizedLabel === 'any room';
        const stepOptions = stepEl.querySelectorAll('.quiz-option');

        if (option.classList.contains('active')) {
          option.classList.remove('active');
          state.application = [];
          state.applicationAny = false;
          updateButtons(stepEl);
          return;
        }

        stepOptions.forEach((o) => o.classList.remove('active'));
        option.classList.add('active');
        state.application = [];
        state.applicationAny = false;

        if (isAnyRoom) {
          state.applicationAny = true;
          updateButtons(stepEl);
          return;
        }

        // Use cached data — instant, no delay
        const allApplicationValues = cachedApplicationFilters || [];
        const matchedValues = allApplicationValues.filter((v) => {
          const normalizedValue = v.toLowerCase();
          return (
            normalizedValue === normalizedLabel ||
            normalizedValue.includes(normalizedLabel) ||
            normalizedLabel.includes(normalizedValue)
          );
        });

        state.application = matchedValues;
        updateButtons(stepEl);
        return;
      }

      // STEP 3 - COLOR (max 2) — unchanged, already instant
      if (step === '3') {
        const label = (option.dataset.label || '').toLowerCase();
        const stepOptions = stepEl.querySelectorAll('.quiz-option');
        const unsureBtn = stepEl.querySelector('.quiz-option[data-label="I\'m not sure"]');

        if (label === "i'm not sure") {
          stepOptions.forEach((o) => o.classList.remove('active'));
          option.classList.add('active');
          state.color = [];
          state.colorUnsure = true;
          updateButtons(stepEl);
          return;
        }

        state.colorUnsure = false;
        if (unsureBtn) unsureBtn.classList.remove('active');

        const activeOptions = stepEl.querySelectorAll('.quiz-option.active:not([data-label="I\'m not sure"])');

        if (!option.classList.contains('active') && activeOptions.length >= limit) return;

        if (option.classList.contains('active')) {
          option.classList.remove('active');
        } else {
          option.classList.add('active');
        }

        state.color = Array.from(
          stepEl.querySelectorAll('.quiz-option.active:not([data-label="I\'m not sure"])')
        ).map((btn) => (btn.dataset.label || '').toLowerCase());

        updateButtons(stepEl);
      }
    });
  });

  quiz.querySelectorAll('.quiz-next').forEach((btn) => {
    btn.addEventListener('click', () => {
      const stepEl = btn.closest('.quiz-step');
      const currentStep = Number(stepEl.dataset.step);
      goToStep(currentStep + 1);
    });
  });

  quiz.querySelectorAll('.quiz-back').forEach((btn) => {
    btn.addEventListener('click', () => {
      const stepEl = btn.closest('.quiz-step');
      const currentStep = Number(stepEl.dataset.step);
      goToStep(currentStep - 1);
    });
  });

  const submitBtn = quiz.querySelector('.quiz-submit');
  if (submitBtn) {
    submitBtn.addEventListener('click', async () => {
      loader.classList.add('active');

      const step3 = document.querySelector("[data-step='3']");
      const finalLoader = document.querySelector('.final_loader');

      if (step3) step3.style.display = 'none';
      if (finalLoader) finalLoader.style.display = 'block';

      let colorGids = [];

      if (!state.colorUnsure && state.color.length > 0) {
        // Use cached color filters — no extra fetch needed
        const colorMap = cachedColorFilters || {};
        colorGids = state.color
          .map((label) => colorMap[label])
          .filter(Boolean)
          .slice(0, 2);
      }

      setTimeout(() => {
        const params = new URLSearchParams();
        params.append('sort_by', 'best-selling');

        if (!state.styleAny) {
          state.style.slice(0, 1).forEach((v) => {
            if (v && v.toLowerCase() !== 'any style') {
              params.append('filter.p.m.custom.style', v);
            }
          });
        }

        if (!state.applicationAny) {
          state.application.forEach((v) => {
            if (v) params.append('filter.p.m.custom.application', v);
          });
        }

        colorGids.forEach((gid) => {
          params.append('filter.v.t.shopify.color-pattern', gid);
        });

        window.location.href = `/collections/${collectionHandle}?${params.toString()}`;
      }, window.quiz_final_timeout || 1200);
    });
  }

  updateProgress(1);
});